<template>
    <VJoyPanel>
        <template #panel-title>
            {{ $t('projectclient.projectview.details.purchase-order.title') }}
        </template>
        <template #panel-subtitle>
            {{ $t('projectclient.projectview.details.purchase-order.subtitle') }}
        </template>
        <template #panel-body>
            <div class="project-details-purchase-order">
                <div ref="purchaseOrderWrapper" class="project-details-purchase-order_input-container / flex">
                    <VJoyInput
                        ref="purchaseOrderInputRef"
                        v-model="purchaseOrder"
                        class="project-details-purchase-order_input-container_input"
                        :placeholder="t('projectclient.projectview.details.purchase-order.title')"
                        name="purchase-order"
                        data-testid="project-view-po-input"
                        :disabled="isProjectEnded"
                        @click="activateSave"
                    />
                    <VJoyButton
                        :disabled="isInputDisabled"
                        :loading="updatingPurchaseOrderNumber"
                        class="project-details-purchase-order_input-container_button"
                        variant="main"
                        size="medium"
                        data-testid="project-view-purchase-order-update-btn"
                        @click="updatePurchaseOrder"
                    >
                        {{ $t('general.save') }}
                    </VJoyButton>
                </div>
                <ValidationNotification
                    v-if="isProjectEnded"
                    class="joy-mt-2"
                    :label="t('projectclient.projectview.details.purchase-order.disabled')"
                    type="neutral"
                />

                <ValidationNotification
                    v-if="isRegexFaulty && purchaseOrder"
                    class="joy-mt-2"
                    :label="t('projectclient.projectview.details.purchase-order.regex')"
                    type="neutral"
                />
            </div>
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {ref, useTemplateRef, onMounted, onActivated, watch, computed} from 'vue';
    import {useTrackPurchaseOrder} from '@common/composables';
    import {useProjectInformationStore} from '@common/stores/project.store';
    import {ValidationNotification} from '@common/ui';
    import {useActivityStore} from '@features/project-view';
    import {useRoute} from '@typed-router';
    import {onClickOutside} from '@vueuse/core';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import {VJoyButton, VJoyInput, VJoyPanel} from '@maltjoy/core-vue';

    const {t} = useTranslation();

    const route = useRoute('project-projectId-index');
    const projectId = route.params.projectId;

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation, updatingPurchaseOrderNumber} = storeToRefs(projectInformationStore);

    const activityStore = useActivityStore();

    const purchaseOrderInputRef = useTemplateRef('purchaseOrderInputRef');
    const purchaseOrderWrapper = useTemplateRef('purchaseOrderWrapper');
    const purchaseOrder = ref(projectInformation.value.clientReference);

    const {resolvePurchaseOrderChange, trackPurchaseOrderAddedOrUpdated, trackPurchaseOrderDeleted} = useTrackPurchaseOrder();

    const isInputDisabled = ref(true);

    const isProjectEnded = computed(() => projectInformation.value.projectStatus === 'ENDED');

    onMounted(() => {
        handlePurchaseOrderAction();
    });

    onActivated(() => {
        projectInformationStore.fetchProjectInformation(projectId);
        handlePurchaseOrderAction();
    });

    function handlePurchaseOrderAction() {
        if (route.query.from === 'tasks') {
            purchaseOrderInputRef.value?.focus();
        }
        activateSave();
    }

    function activateSave() {
        // disable po update for projects that are over
        if (isProjectEnded.value) return;
        isInputDisabled.value = false;
    }

    onClickOutside(purchaseOrderWrapper, () => {
        isInputDisabled.value = true;
    });

    async function updatePurchaseOrder() {
        const purchaseOrderChange = resolvePurchaseOrderChange(projectInformation.value.clientReference, purchaseOrder.value);
        if (!purchaseOrder.value && !isProjectEnded.value) {
            const result = await projectInformationStore.deletePurchaseOrder({projectId});
            if (purchaseOrderChange === 'deleted' && result === 'applied') {
                await trackPurchaseOrderDeleted('client_project_view');
            }
        } else if (purchaseOrder.value && !isProjectEnded.value) {
            const result = await projectInformationStore.putPurchaseOrder({
                projectId: projectInformation.value.projectId,
                purchaseOrderParam: {
                    purchaseOrderNumber: purchaseOrder.value.trim() ?? '',
                },
            });
            if (result === 'applied') {
                await trackPurchaseOrderAddedOrUpdated(
                    purchaseOrderChange,
                    'client_project_view',
                    projectInformation.value.lot.purchaseOrderMandatory,
                );
            }
            if (result === 'too-late') {
                purchaseOrder.value = projectInformation.value.clientReference ?? '';
            }
        }
        isInputDisabled.value = true;
        await activityStore.fetchProjectActivity();
        await projectInformationStore.fetchProjectInformation(projectId);
    }

    // handle purchase order input
    const isRegexFaulty = ref(false);
    const regex = /^(?=.*\d)[\w-]{1,25}$/;

    watch(
        () => purchaseOrder.value,
        () => {
            if (purchaseOrder.value) {
                isRegexFaulty.value = !regex.test(purchaseOrder.value.trim());
            }
        },
    );
</script>

<style lang="scss" scoped>
    .project-details-purchase-order {
        &_input-container {
            align-items: center;
            gap: var(--joy-core-spacing-4);
            padding: var(--joy-core-spacing-2) 0 0 0;
            width: fit-content;

            :deep(input) {
                width: 300px !important;

                &:hover {
                    cursor: pointer;
                }
            }

            :deep(.joy-input--wrapper) {
                margin: 0;
            }
        }
    }
</style>
